import type { TitleBrief } from "@anisync/types";
import { KeyboardIcon, SearchIcon } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router";
import { TitleCard, TitleCardSkeleton } from "../components/title-card";
import { Alert, AlertDescription } from "../components/ui/alert";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { ApiError, searchTitles } from "../lib/api";
import { readFlag, writeFlag } from "../lib/device-pref";
import { OnScreenKeyboard } from "../tv/on-screen-keyboard";

/** Odczekanie przed zapytaniem — na tyle krótkie, by nie drażniło, na tyle
 *  długie, by pisanie nie generowało zapytania na każdą literę. */
const DEBOUNCE_MS = 350;

/** Stałe klucze zastępczych kafli — indeks tablicy nie nadaje się na klucz. */
const SKELETON_KEYS = ["s1", "s2", "s3", "s4", "s5", "s6", "s7", "s8"] as const;

/** Klawiatura ekranowa zostaje włączona na tym urządzeniu, aż ktoś ją wyłączy. */
const KEYBOARD_PREF = "onScreenKeyboard";

export function SearchScreen() {
  const [params, setParams] = useSearchParams();
  const initial = params.get("q") ?? "";
  const [input, setInput] = useState(initial);
  const [results, setResults] = useState<TitleBrief[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [keyboard, setKeyboard] = useState(() => readFlag(KEYBOARD_PREF, false));
  const inputRef = useRef<HTMLInputElement>(null);

  /**
   * Fokus na pole tylko wtedy, gdy pole jest sposobem pisania.
   *
   * Przy włączonej klawiaturze ekranowej fokus bierze jej pierwszy klawisz — pole
   * pilotem nie przyjmie znaku, więc oddanie mu fokusu byłoby postawieniem widza
   * przed kontrolką, która nie reaguje.
   */
  useEffect(() => {
    if (!keyboard) inputRef.current?.focus();
  }, [keyboard]);

  useEffect(() => {
    const query = input.trim();
    if (query.length === 0) {
      setResults(null);
      setError(null);
      setLoading(false);
      return;
    }

    setLoading(true);
    const timer = setTimeout(() => {
      // Kolejność odpowiedzi nie jest gwarantowana, więc odrzucamy wyniki
      // zapytania, które w międzyczasie przestało być aktualne.
      let stale = false;
      searchTitles(query)
        .then((hits) => {
          if (stale) return;
          setResults(hits);
          setError(null);
        })
        .catch((e: unknown) => {
          if (stale) return;
          setResults([]);
          setError(e instanceof ApiError ? e.message : "Nie udało się wyszukać");
        })
        .finally(() => {
          if (!stale) setLoading(false);
        });
      return () => {
        stale = true;
      };
    }, DEBOUNCE_MS);

    return () => clearTimeout(timer);
  }, [input]);

  function commit(value: string) {
    setInput(value);
    // Zapytanie trafia do adresu, więc wynik da się odświeżyć i udostępnić.
    if (value.trim().length > 0) setParams({ q: value }, { replace: true });
    else setParams({}, { replace: true });
  }

  return (
    <div className="flex flex-col gap-8">
      <div className="flex flex-col gap-3">
        <label
          htmlFor="q"
          className="text-sm font-semibold tracking-wide text-muted-foreground uppercase"
        >
          Szukaj anime
        </label>
        <div className="relative w-full max-w-2xl">
          <SearchIcon
            aria-hidden="true"
            className="pointer-events-none absolute top-1/2 left-4 size-5 -translate-y-1/2 text-muted-foreground"
          />
          <Input
            id="q"
            ref={inputRef}
            type="search"
            value={input}
            onChange={(e) => commit(e.target.value)}
            placeholder="Tytuł po angielsku, romaji albo japońsku…"
            autoComplete="off"
            spellCheck={false}
            className="h-14 rounded-lg pl-12 text-lg"
          />
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <p className="text-sm text-muted-foreground">
            Wyszukiwanie łączy katalog AniList z tym, co już jest w lokalnym lustrze.
          </p>
          <Button
            type="button"
            variant={keyboard ? "secondary" : "ghost"}
            size="sm"
            className="gap-2"
            aria-pressed={keyboard}
            onClick={() => {
              const next = !keyboard;
              setKeyboard(next);
              writeFlag(KEYBOARD_PREF, next);
            }}
          >
            <KeyboardIcon aria-hidden="true" />
            {keyboard ? "Schowaj klawiaturę" : "Klawiatura ekranowa"}
          </Button>
        </div>

        {/**
         * Przełącznik jest widoczny zawsze, także przy myszy i klawiaturze.
         *
         * Wersja pierwsza pokazywała klawiaturę w trybie TV. To ten sam błąd, który
         * CLAUDE.md odnotowuje przy sterowaniu pilotem: żeby włączyć tryb TV, trzeba
         * dojść do ustawień — pilotem, czyli z pomocą tego, czego jeszcze nie ma.
         * Przycisk na ekranie, na którym klawiatura jest potrzebna, znajduje się
         * strzałkami w dwóch naciśnięciach.
         */}
        {keyboard && <OnScreenKeyboard value={input} onChange={commit} autoFocusFirstKey />}
      </div>

      {error !== null && (
        <Alert variant="destructive">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {loading && results === null && (
        <div className="tile-grid gap-4">
          {SKELETON_KEYS.map((k) => (
            <TitleCardSkeleton key={k} fluid />
          ))}
        </div>
      )}

      {results !== null && results.length === 0 && !loading && error === null && (
        <p className="text-muted-foreground">Brak wyników dla „{input.trim()}".</p>
      )}

      {results !== null && results.length > 0 && (
        <div>
          <p className="mb-4 text-sm text-muted-foreground">
            {results.length === 1 ? "1 wynik" : `${results.length} wyników`}
            {loading && " · odświeżam…"}
          </p>
          <div className="tile-grid gap-4">
            {results.map((t, i) => (
              <TitleCard key={t.id} title={t} fluid index={i} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
